import { Modal } from '@design/components/modal/Modal';
import { useCustomSources } from '@terminal/react-query-hooks/alfred/useCustomSources';
import { POSTHOG_TRACKING_SOURCES } from '@terminal/services/tracking/posthog/constants';
import { usePosthogEvents } from '@terminal/services/tracking/posthog/use-posthog-events/usePosthogEvents';
import {
  ToastPosition,
  useToasts,
} from '@terminal/stores/use-toasts/useToasts';
import { updateCustomSources } from '@terminal/views/alfred/research/services/conversations-service';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { formatWebsiteInfo, WebsiteInfo } from '../utils/formatWebsiteInfo';
import { InlineAlert } from './InlineAlert';
import { LinkInput } from './LinkInput';
import { MAX_CUSTOM_SOURCES, SourcesArea } from './SourcesArea';
import { SourcesUpdateAlert } from './SourcesUpdateAlert';
import styles from './TrustedSourcesModal.module.scss';

interface TrustedSourcesModalProps {
  isOpen: boolean;
  onClose: () => void;
}

export const TrustedSourcesModal = ({
  isOpen,
  onClose,
}: TrustedSourcesModalProps) => {
  const { t } = useTranslation(['chat', 'common']);

  const [customSources, setCustomSources] = useState<WebsiteInfo[]>([]);
  const [hasUserModified, setHasUserModified] = useState(false);

  const { trackAlfredTrustedSourceAdded, trackAlfredTrustedSourceRemoved } =
    usePosthogEvents();
  const showToast = useToasts(state => state.showToast);

  const { data: customSourcesData, refetch: refetchCustomSources } =
    useCustomSources(isOpen);

  useEffect(() => {
    if (customSourcesData?.data?.sources) {
      setCustomSources(
        formatWebsiteInfo(customSourcesData?.data?.sources ?? [])
      );
    }
  }, [customSourcesData]);

  const handleAddWebsites = (websites: WebsiteInfo[]) => {
    const existedSources = customSources.map(source => source.url);
    const uniqueWebsites = websites.filter(
      website => !existedSources.includes(website.url)
    );

    if (uniqueWebsites.length > 0) {
      trackAlfredTrustedSourceAdded(POSTHOG_TRACKING_SOURCES.ALFRED);
      const newSources = [...customSources, ...uniqueWebsites];
      setCustomSources(newSources);
      setHasUserModified(true);
    }
  };

  const handleClose = () => {
    setCustomSources([]);
    setHasUserModified(false);
    onClose();
  };

  const handleUpdateCustomSources = async () => {
    const response = await updateCustomSources({
      sources: customSources.map(source => source.url),
    });

    if (response) {
      handleClose();
    }

    if (response.data) {
      refetchCustomSources();

      showToast({
        id: 'update-sources-success',
        position: ToastPosition.TopRight,
        content: <SourcesUpdateAlert />,
      });
    }
  };

  const handleRemoveSource = (id: string) => {
    trackAlfredTrustedSourceRemoved(POSTHOG_TRACKING_SOURCES.ALFRED);
    setCustomSources(customSources.filter(source => source.id !== id));
    setHasUserModified(true);
  };

  const getModalProps = () => {
    return {
      title: t('chat:trustedSources.modal.title'),
      description: (
        <p>
          {t('chat:trustedSources.modal.description_1_rapid')}
          <br />
          <br />
          {t('chat:trustedSources.modal.description_2')}
        </p>
      ),
      ...(hasUserModified && {
        primaryBtnProps: {
          label: t('chat:trustedSources.modal.saveTrustedSources'),
          onClick: handleUpdateCustomSources,
        },
        cancelBtnProps: {
          label: t('common:cancel'),
          onClick: handleClose,
        },
      }),
    };
  };

  return (
    <Modal
      className={styles.styledModal}
      isOpen={isOpen}
      onClose={handleClose}
      {...getModalProps()}
      disableEnterClose
      outsideClickClose={false}
      data-testid="trusted-sources-modal"
    >
      <div className={styles.content}>
        {customSources.length > 0 && (
          <div className={styles.sources}>
            <SourcesArea
              items={customSources}
              handleRemove={handleRemoveSource}
            />
          </div>
        )}
        {customSources.length >= MAX_CUSTOM_SOURCES ? (
          <InlineAlert />
        ) : (
          <LinkInput
            sourceCount={customSources.length}
            handleAddWebsites={handleAddWebsites}
          />
        )}
      </div>
    </Modal>
  );
};
